// Custom hook for WhatsApp message management
import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, startAfter, endBefore, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { extractMessageInfo, extractPhoneNumber, groupMessagesByPhone } from '../utils/whatsappUtils';
import { getUnifiedTimestamp, combineMessages, groupCombinedMessagesByPhone, filterContactsForDisplay } from '../utils/combinedMessageUtils';

/**
 * Hook for managing WhatsApp messages
 */
export const useWhatsAppMessages = (whatsappAllowed) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [messages, setMessages] = useState([]);
  const [groupedChats, setGroupedChats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageInfo, setPageInfo] = useState({ page: 1, isLast: false });
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);
  const [filterButtonMessages, setFilterButtonMessages] = useState(true);
  const [outgoingMessages, setOutgoingMessages] = useState([]);
  const [combinedMessages, setCombinedMessages] = useState([]);
  const [unifiedContacts, setUnifiedContacts] = useState({});
  const [selectedContact, setSelectedContact] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // Filter contacts for display (preserves names from hidden button messages)
  const filteredUnifiedContacts = filterContactsForDisplay(unifiedContacts, filterButtonMessages, searchTerm);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (whatsappAllowed) {
      loadMessages();
    }
  }, [whatsappAllowed]);

  useEffect(() => {
    if (messages.length > 0) {
      const grouped = groupMessagesByPhone(messages);
      setGroupedChats(grouped);

      // Auto-select first chat if none selected
      if (!selectedContact && Object.keys(grouped).length > 0) {
        const firstChat = Object.keys(grouped)[0];
        setSelectedContact(firstChat);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (combinedMessages.length > 0) {
      const grouped = groupCombinedMessagesByPhone(combinedMessages);
      setUnifiedContacts(grouped);

      if (!selectedContact && Object.keys(grouped).length > 0) {
        const firstChat = Object.keys(grouped)[0];
        setSelectedContact(firstChat);
      }
    }
  }, [combinedMessages]);

  useEffect(() => {
    // Reload messages whenever search term changes (including when cleared)
    loadMessages();
  }, [searchTerm]);

  useEffect(() => {
    // Reload messages whenever filter state changes
    loadMessages();
  }, [filterButtonMessages]);

  // ============================================================================
  // DATA LOADING FUNCTIONS
  // ============================================================================

  const loadOutgoingMessages = async () => {
    if (!whatsappAllowed) return [];

    try {
      const q = query(
        collection(db, 'x_sendmsg'),
        orderBy('updatedAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();

        return {
          _raw: doc,
          id: doc.id,
          timestamp: data.updatedAt || data.created_at,
          phone: extractPhoneNumber(data.phoneno || ''),
          name: data.sender || 'Agent',
          body: data.msgbody || '',
          type: data.process_name || 'text',
          data: data,
          meta: {
            phoneno: data.phoneno || '',
            sender: data.sender || '',
            send_code: data.send_code || '',
            reference: data.reference || '',
            messageSid: data.messageSid || '',
            process_name: data.process_name || ''
          },
          direction: 'outgoing',
          unified_timestamp: getUnifiedTimestamp(data.updatedAt || data.created_at)
        };
      }).filter(msg => msg !== null);
    } catch (err) {
      console.error('Error loading outgoing messages:', err);
      return [];
    }
  };

  const loadIncomingMessages = async (direction = 'forward') => {
    if (!whatsappAllowed) return [];

    try {
      let q = query(
        collection(db, 'whatsapp_incoming'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );

      if (direction === 'next' && lastVisible) {
        q = query(
          collection(db, 'whatsapp_incoming'),
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(50)
        );
      } else if (direction === 'prev' && firstVisible) {
        q = query(
          collection(db, 'whatsapp_incoming'),
          orderBy('timestamp', 'asc'),
          startAfter(firstVisible),
          limit(50)
        );
      }

      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map((doc) => {
        const data = doc.data() || {};
        const ts = data.timestamp || data.createdAt || null;
        return {
          _raw: doc,
          id: doc.id,
          payload: data.payload || {},
          timestamp: ts,
          meta: {
            from: data.payload?.From || data.payload?.from || data.from || '',
            waId: data.payload?.WaId || '',
          },
        };
      });

      // Update pagination cursors
      if (docs.length > 0) {
        if (direction === 'next') {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        } else if (direction === 'prev') {
          setFirstVisible(snapshot.docs[snapshot.docs.length - 1]);
        } else {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
          setFirstVisible(snapshot.docs[0]);
        }
      }

      // Return ALL messages (including button messages) for proper name extraction and grouping
      // Button message filter will be applied at display level, not data level
      return docs;
    } catch (err) {
      console.error('Error loading incoming messages:', err);
      setError(`Error loading messages: ${err.message}`);
      return [];
    }
  };

  const loadMessages = async (direction = 'forward') => {
    if (!whatsappAllowed) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load both incoming and outgoing messages in parallel
      const [incomingMsgs, outgoingMsgs] = await Promise.all([
        loadIncomingMessages(direction),
        loadOutgoingMessages()
      ]);

      // Combine and set unified data
      const combined = combineMessages(incomingMsgs, outgoingMsgs);
      setCombinedMessages(combined);

      // Update pagination cursors (keep existing logic for incoming)
      if (incomingMsgs.length > 0) {
        if (direction === 'next') {
          // setLastVisible already handled in loadIncomingMessages
        } else if (direction === 'prev') {
          // setFirstVisible already handled in loadIncomingMessages
        } else {
          // Initial load - cursors already set in loadIncomingMessages
        }
      }

      setMessages(prev => {
        if (direction === 'next') {
          return [...prev, ...incomingMsgs];
        } else if (direction === 'prev') {
          return [...incomingMsgs, ...prev];
        } else {
          return incomingMsgs;
        }
      });

      setPageInfo({
        page: direction === 'next' ? pageInfo.page + 1 : direction === 'prev' ? pageInfo.page - 1 : 1,
        isLast: incomingMsgs.length < 50
      });

    } catch (err) {
      console.error('Error loading messages:', err);
      setError(`Error loading messages: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    groupedChats,
    searchTerm,
    setSearchTerm,
    isLoading,
    error,
    pageInfo,
    lastVisible,
    firstVisible,
    filterButtonMessages,
    setFilterButtonMessages,
    selectedContact,
    setSelectedContact,
    showSendModal,
    setShowSendModal,
    outgoingMessages,
    combinedMessages,
    unifiedContacts,
    filteredUnifiedContacts,
    loadMessages
  };
};
